import React, { useState, useRef, useEffect } from 'react';
import Input from 'components/ui/Input';
import { Checkbox } from 'components/ui/Checkbox';
import { cn } from '../../../utils/cn';
import { X } from 'lucide-react';

const WorkAreaSection = ({ workArea, worksInAllItaly, onChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [extendedAvailability, setExtendedAvailability] = useState(false);
  const [allItaly, setAllItaly] = useState(false);
  const [includeSardiniaSicily, setIncludeSardiniaSicily] = useState(false);
  const [sessionToken, setSessionToken] = useState(() => generateSessionToken());
  const searchTimeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  // TODO: This fallback token is temporary for development/preview.
  // In production, VITE_MAPBOX_TOKEN must be set via environment variables
  // or replaced with a backend proxy to keep the token secure.
  const MAPBOX_TOKEN = (() => {
    const envToken = import.meta.env?.VITE_MAPBOX_TOKEN;
    // Check if env token exists and is not a placeholder
    if (envToken && envToken !== 'your-mapbox-token-here' && !envToken?.includes('placeholder')) {
      return envToken;
    }
    // Fallback to development token
    return 'pk.eyJ1IjoicGVkZXJ6b2xsaXNpbW9uZSIsImEiOiJjbWt5OTA3OHowNG02M2VzZTFoYWJwN3E2In0.HCcTrRyl9L0dWbcIp0x1iQ';
  })();

  // Generate a unique session token for Mapbox Search Box API
  function generateSessionToken() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'?.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v?.toString(16);
    });
  }

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef?.current && !wrapperRef?.current?.contains(event?.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const searchLocations = async (query) => {
    if (!query || query?.length < 2) {
      setSuggestions([]);
      return;
    }

    if (!MAPBOX_TOKEN) {
      console.error('Mapbox token not found');
      return;
    }

    setIsLoading(true);

    try {
      const url = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(query)}&language=it&country=IT&types=region,district,place,locality&limit=5&session_token=${sessionToken}&access_token=${MAPBOX_TOKEN}`;
      
      const response = await fetch(url);

      if (!response?.ok) {
        const errorText = await response?.text();
        console.error('Mapbox API error:', {
          status: response?.status,
          statusText: response?.statusText,
          body: errorText
        });
        throw new Error(`Mapbox API returned ${response.status}: ${response.statusText}`);
      }

      const data = await response?.json();
      setSuggestions(data?.suggestions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching location suggestions:', error?.message || error);
      setSuggestions([]);
      // Don't show error to user for better UX, just log it
    } finally {
      setIsLoading(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);

    if (searchTimeoutRef?.current) {
      clearTimeout(searchTimeoutRef?.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocations(value);
    }, 300);
  };

  // Retrieve full location details
  const retrieveLocation = async (mapboxId) => {
    if (!MAPBOX_TOKEN) return null;

    try {
      const response = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${mapboxId}?session_token=${sessionToken}&access_token=${MAPBOX_TOKEN}`
      );

      if (!response?.ok) {
        throw new Error('Failed to retrieve location');
      }

      const data = await response?.json();
      return data?.features?.[0];
    } catch (error) {
      console.error('Error retrieving location:', error);
      return null;
    }
  };

  // Handle location selection
  const handleSelectLocation = async (suggestion) => {
    if (selectedAreas?.length >= 3) {
      return;
    }

    // Check if already selected
    const alreadySelected = selectedAreas?.some(
      (area) => area?.mapbox_id === suggestion?.mapbox_id
    );

    if (alreadySelected) {
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Retrieve full location details
    const locationDetails = await retrieveLocation(suggestion?.mapbox_id);

    const newArea = {
      id: suggestion?.mapbox_id,
      name: suggestion?.name,
      fullName: suggestion?.full_address || suggestion?.name,
      mapbox_id: suggestion?.mapbox_id,
      details: locationDetails,
    };

    const updatedAreas = [...selectedAreas, newArea];
    setSelectedAreas(updatedAreas);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);

    // Generate new session token after selection (best practice for Mapbox Search Box API)
    setSessionToken(generateSessionToken());

    // Notify parent component
    onChange?.('primaryWorkAreas', updatedAreas);
  };

  // Remove selected area
  const handleRemoveArea = (areaId) => {
    const updatedAreas = selectedAreas?.filter((area) => area?.id !== areaId);
    setSelectedAreas(updatedAreas);
    onChange?.('primaryWorkAreas', updatedAreas);
  };

  // Handle extended availability change
  const handleExtendedAvailabilityChange = (checked) => {
    setExtendedAvailability(checked);
    if (!checked) {
      setAllItaly(false);
      setIncludeSardiniaSicily(false);
    }
    onChange?.('extendedAvailability', {
      enabled: checked,
      allItaly: checked ? allItaly : false,
      includeSardiniaSicily: checked ? includeSardiniaSicily : false,
    });
  };

  const handleAllItalyChange = (checked) => {
    setAllItaly(checked);
    onChange?.('extendedAvailability', {
      enabled: extendedAvailability,
      allItaly: checked,
      includeSardiniaSicily,
    });
  };

  const handleIncludeSardiniaSicilyChange = (checked) => {
    setIncludeSardiniaSicily(checked);
    onChange?.('extendedAvailability', {
      enabled: extendedAvailability,
      allItaly,
      includeSardiniaSicily: checked,
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">
        Area di lavoro
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Indica la zona geografica in cui operi abitualmente. Questo ci aiuterà a
        proporti richieste di lavoro nella tua area.
      </p>
      <div className="space-y-4">
        {/* Primary Work Areas */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Zone primarie di lavoro
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Seleziona le zone dove operi di norma (max 3)
          </p>

          {/* Search Input */}
          <div className="relative" ref={wrapperRef}>
            <Input
              type="text"
              placeholder="Cerca città, provincia o regione..."
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={selectedAreas?.length >= 3}
              className={cn(
                selectedAreas?.length >= 3 && 'bg-gray-100 cursor-not-allowed'
              )}
            />

            {/* Loading indicator */}
            {isLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
              </div>
            )}

            {/* Suggestions dropdown */}
            {showSuggestions && suggestions?.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {suggestions?.map((suggestion) => {
                  const isSelected = selectedAreas?.some(
                    (area) => area?.mapbox_id === suggestion?.mapbox_id
                  );

                  return (
                    <button
                      key={suggestion?.mapbox_id}
                      type="button"
                      onClick={() => handleSelectLocation(suggestion)}
                      disabled={isSelected || selectedAreas?.length >= 3}
                      className={cn(
                        'w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors',
                        isSelected && 'bg-gray-50 text-gray-400 cursor-not-allowed',
                        selectedAreas?.length >= 3 &&
                          !isSelected &&
                          'cursor-not-allowed opacity-50'
                      )}
                    >
                      <div className="font-medium text-sm text-gray-900">
                        {suggestion?.name}
                      </div>
                      {suggestion?.place_formatted && (
                        <div className="text-xs text-gray-500">
                          {suggestion?.place_formatted}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected areas chips */}
          {selectedAreas?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedAreas?.map((area) => (
                <div
                  key={area?.id}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                >
                  <span>{area?.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveArea(area?.id)}
                    className="ml-1 hover:bg-blue-100 rounded-full p-0.5 transition-colors"
                    aria-label={`Rimuovi ${area?.name}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Counter */}
          <div className="mt-2 text-sm text-gray-600">
            {selectedAreas?.length}/3 zone selezionate
          </div>
        </div>

        {/* Extended Availability */}
        <div className="pt-4 border-t border-gray-200">
          <Checkbox
            id="extended-availability"
            checked={extendedAvailability}
            onChange={(e) => handleExtendedAvailabilityChange(e?.target?.checked)}
            label="Valuto anche richieste fuori zona"
          />

          {extendedAvailability && (
            <div className="mt-3 ml-6 space-y-2">
              <Checkbox
                id="all-italy"
                checked={allItaly}
                onChange={(e) => handleAllItalyChange(e?.target?.checked)}
                label="In tutta Italia"
              />

              <Checkbox
                id="include-sardinia-sicily"
                checked={includeSardiniaSicily}
                onChange={(e) =>
                  handleIncludeSardiniaSicilyChange(e?.target?.checked)
                }
                label="Include Sardegna e Sicilia"
              />

              <p className="text-xs text-gray-500 mt-2">
                Queste opzioni indicano solo disponibilità a valutare richieste,
                non garantiscono accettazione.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkAreaSection;